import { setError, setLoaded, setLoading, withRequestState } from '@iris/common/signals/features/request-status.feature';
import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';
import { setAllEntities, setEntities, withEntities } from '@ngrx/signals/entities';
import { IrisDMSFileI } from '../models/IrisDMSFile';
import { computed, inject } from '@angular/core';
import { IrisFilesService } from '../services/files.service';
import { debounceTime, distinctUntilChanged, filter, pipe, switchMap } from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { HttpError } from '@microsoft/signalr';
import { IrisDmsFilesSandbox } from '../sandbox/dms-files.sandbox';

export const IrisDmsLinkedFilesStore = signalStore(
  { providedIn: 'root' },
  withEntities<IrisDMSFileI>(),
  withRequestState(),
  withComputed(store => ({
    files: store.entities,
    hasLinkedFiles: computed(() => !!store.entities().length),
  })),
  withMethods((
    store,
    filesService = inject(IrisFilesService),
    dmsFilesSandbox = inject(IrisDmsFilesSandbox),
  ) => ({
    getFileById: (fileId: string) => store.entityMap()[fileId],
    loadLinkedFiles: rxMethod<string>(
      pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(() => !store.loading()),
        switchMap(fileId => {
          patchState(store, setLoading());

          return filesService.getLinkedFiles(fileId).pipe(
            tapResponse({
              next: files => {
                patchState(
                  store,
                  setAllEntities(files),
                  setLoaded(),
                );
              },
              error: (err: HttpError) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    linkFiles: rxMethod<{ fileId: string; files: IrisDMSFileI[] }>(
      pipe(
        distinctUntilChanged(),
        switchMap(({ fileId, files }) => {
          return filesService.linkFiles(fileId, files.map(file => file.id)).pipe(
            tapResponse({
              next: (files) => {
                patchState(
                  store,
                  setEntities(files),
                );
                dmsFilesSandbox.fetchFileById(fileId);
                files.forEach(file => dmsFilesSandbox.updateEntity(file));
              },
              error: (err: HttpError) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    unlinkFile: rxMethod<{ fileId: string; linkedFileId: string }>(
      pipe(
        distinctUntilChanged(),
        switchMap(({ fileId, linkedFileId }) => {
          return filesService.unlinkFile(fileId, linkedFileId).pipe(
            tapResponse({
              next: (files) => {
                patchState(
                  store,
                  setAllEntities(files),
                );
                const filesToFetch = [fileId];
                if (dmsFilesSandbox.isFileVisible(linkedFileId)) {
                  filesToFetch.push(linkedFileId);
                }
                dmsFilesSandbox.fetchFilesByIds(filesToFetch);
              },
              error: (err: HttpError) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
  })),
);
